// utils/bridge.js
//
// Single entry point for native-shell integration (see docs/ARQUITECTURA-ONLINE.md §1).
// The Tauri shell injects `window.csBridge` (and `window.electron` for compat with the
// old Electron preload contract) before any page script runs. In the browser, with no
// shell present, we fall back to `mockBridge`, which implements the SAME contract using
// `localStorage` for config storage and safe no-ops for anything that needs to touch the
// local filesystem / game folder.
//
// Contract (11 methods — keep in sync with the shell repo if it ever changes):
//   getAppVersion, getAppInitStatus, saveLocalConfig, listLocalConfigs, deleteLocalConfig,
//   detectCS2Path, installConfigsToGame, launchCS2, openFolder, selectFolder, openExternal
//
// Rule: the rest of the app should NEVER touch `window.electron` / `window.csBridge`
// directly — always go through `getBridge()`.

import { v4 as uuidv4 } from 'uuid';

const LOCAL_STORAGE_KEY = 'cs2_configs';

function readLocalConfigs() {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch (error) {
        console.error('[bridge] Error reading local configs:', error);
        return [];
    }
}

function writeLocalConfigs(configs) {
    try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(configs));
    } catch (error) {
        console.error('[bridge] Error writing local configs:', error);
    }
}

// Mock bridge used in plain browser (no shell). Same contract as the shell bridge,
// but config storage lives in localStorage and anything that would touch the game
// folder / filesystem is a safe no-op.
export const mockBridge = {
    isMock: true,

    async getAppVersion() {
        return 'web';
    },

    async getAppInitStatus() {
        return { savePath: null };
    },

    async saveLocalConfig(config) {
        try {
            const configs = readLocalConfigs();
            if (!config.id) config.id = uuidv4();
            // Preserve an explicit updated_at instead of always stamping "now" —
            // the sync engine (utils/sync/collect.js) re-applies incoming records
            // with their real LWW clock; a normal user save never sets this field
            // itself, so it still gets stamped fresh below.
            if (!config.updated_at) config.updated_at = new Date().toISOString();
            if (!config.settings) config.settings = {};

            const idx = configs.findIndex((c) => c.id === config.id);
            if (idx >= 0) configs[idx] = config;
            else configs.push(config);

            writeLocalConfigs(configs);
            return { success: true, message: 'Guardado en el navegador', id: config.id };
        } catch (error) {
            console.error('[bridge] Error saving local config:', error);
            return { success: false, message: error.message };
        }
    },

    async listLocalConfigs() {
        const configs = readLocalConfigs();
        return [...configs].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    },

    async deleteLocalConfig({ id }) {
        try {
            const configs = readLocalConfigs();
            const next = configs.filter((c) => c.id !== id);
            if (next.length === configs.length) {
                return { success: false, message: 'Config not found' };
            }
            writeLocalConfigs(next);
            return { success: true };
        } catch (error) {
            console.error('[bridge] Error deleting local config:', error);
            return { success: false, message: error.message };
        }
    },

    // Everything below touches the game folder / local filesystem — not possible
    // from a plain browser tab, so these are deliberate no-ops.
    async detectCS2Path() {
        return null;
    },

    async installConfigsToGame() {
        return { success: false, message: 'Solo disponible en la app de escritorio' };
    },

    async launchCS2() {
        return { success: false, message: 'Solo disponible en la app de escritorio' };
    },

    async openFolder() {
        return false;
    },

    async selectFolder() {
        return null;
    },

    // Opening external links IS possible from the browser.
    async openExternal(url) {
        try {
            window.open(url, '_blank', 'noopener,noreferrer');
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
};

// True when running inside the native shell (Tauri, or the legacy Electron build
// during the transition). SSR-safe.
export function isShell() {
    if (typeof window === 'undefined') return false;
    return !!(window.csBridge || window.electron?.isElectron || window.electron?.isConfigStrikeShell);
}

// SSR-safe bridge getter. This is the ONLY place that should read
// `window.csBridge` / `window.electron` — everything else calls getBridge().
export function getBridge() {
    if (typeof window === 'undefined') return null;
    return window.csBridge ?? window.electron ?? mockBridge;
}

// Capabilities the UI can use to show/hide native-only buttons. Getters so they
// always reflect the current environment instead of a value frozen at import time.
export const capabilities = {
    get installToGame() {
        return isShell();
    },
    get launch() {
        return isShell();
    },
    get openFolder() {
        return isShell();
    },
    get detectPath() {
        return isShell();
    },
};
